import type { CommissionArrangement } from './sessionTakings';

/**
 * Who changes an artist's commission, and what a valid one looks like (B9 /
 * 14ym9bv75nk). Commission is set per artist on their profile (diagram 10,
 * DECIDED 13 Aug). Anas, 1 Oct: Owner and Ops may change it, checked by the
 * server; a change applies to any day not yet paid, and past payouts keep the
 * rate they were paid at (payout records snapshot the arrangement).
 *
 * The one rule for the profile form and PUT /api/artists/:id.
 */

/** Personas who may change an artist's commission: the Owner and Ops (Georgina). */
export const COMMISSION_EDITORS: ReadonlySet<string> = new Set(['owner', 'georgina']);

/** The largest flat day fee accepted: a typo guard, well above any real day fee. */
export const MAX_FLAT_DAY_FEE_AUD = 5000;

export interface CommissionFields {
  commissionRate?: unknown;
  commissionArrangement?: unknown;
}

export type CommissionCheck = { ok: true } | { ok: false; field: 'commissionRate' | 'commissionArrangement'; message: string };

const isShare = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
const keysAre = (o: object, allowed: string[]): boolean => Object.keys(o).every((k) => allowed.includes(k));

/** Is this a well-formed commission (rate 0..1; arrangement one of the three kinds, no extra keys)? */
export function validateCommission(fields: CommissionFields): CommissionCheck {
  if (!isShare(fields.commissionRate)) {
    return { ok: false, field: 'commissionRate', message: 'The commission rate must be between 0 and 1.' };
  }
  const a = fields.commissionArrangement;
  if (a === undefined || a === null) return { ok: true };
  const bad = (message: string): CommissionCheck => ({ ok: false, field: 'commissionArrangement', message });
  if (typeof a !== 'object' || Array.isArray(a)) return bad('The commission arrangement is not recognised.');
  const arr = a as Record<string, unknown>;
  switch (arr.kind) {
    case 'percentage':
      if (!keysAre(arr, ['kind', 'artistShare'])) return bad('A percentage arrangement only carries the artist share.');
      return isShare(arr.artistShare) ? { ok: true } : bad("The artist's share must be between 0% and 100%.");
    case 'flat-day-fee':
      if (!keysAre(arr, ['kind', 'feeAud'])) return bad('A flat day fee only carries the fee.');
      return typeof arr.feeAud === 'number' && Number.isFinite(arr.feeAud) && arr.feeAud >= 0 && arr.feeAud <= MAX_FLAT_DAY_FEE_AUD
        ? { ok: true }
        : bad(`The flat day fee must be between $0 and $${MAX_FLAT_DAY_FEE_AUD}.`);
    case 'owner-artist':
      return keysAre(arr, ['kind']) ? { ok: true } : bad('An owner-artist arrangement carries nothing else.');
    default:
      return bad('The commission arrangement is not recognised.');
  }
}

/** Key-order-insensitive JSON, so a jsonb column and a client object compare equal. */
function canonical(v: unknown): string {
  if (v === undefined || v === null) return 'null';
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/**
 * Does `next` change the commission `stored` holds? A db numeric string equals
 * its number, key order doesn't matter, and a null arrangement equals an absent
 * one. No stored artist (a create) counts as a change.
 */
export function commissionChanged(next: CommissionFields, stored: CommissionFields | undefined): boolean {
  if (!stored) return true;
  if (Number(next.commissionRate) !== Number(stored.commissionRate)) return true;
  return canonical(next.commissionArrangement) !== canonical(stored.commissionArrangement);
}

/** Plain words for an arrangement (activity feed, confirmations). */
export function describeArrangement(a: CommissionArrangement): string {
  switch (a.kind) {
    case 'percentage':
      return `${Math.round(a.artistShare * 100)}% to the artist`;
    case 'flat-day-fee':
      return `flat $${a.feeAud} a day`;
    case 'owner-artist':
      return 'keeps 100%';
  }
}
